import React, { useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";
import { et } from "date-fns/locale";
import { Files, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  formatClusterDateRange,
  type ServerTimelineCluster as ServerTimelineClusterType,
} from "@/domains/documents/utils/timelineClustering";
import type {
  ExpandedClusterState,
  DocumentRowMapped,
  TimelineGranularity,
} from "@/stores/documents/repository";
import { Button } from "@/components/ui/button";
import { TimelineDot, TimelineConnector } from "../../atoms/Timeline";
import { ServerTimelineNode } from "./ServerTimelineNode";
import type { DocumentNodeProps } from "./ServerTimelineDayGroup";

interface ServerTimelineClusterProps {
  /** The cluster data from server-side buckets */
  cluster: ServerTimelineClusterType;
  /** Which side of the timeline stem */
  side: "left" | "right";
  /** Pixel position from top of container */
  pixelPosition: number;
  /** Whether the inline document list is open (controlled) */
  isInlineOpen: boolean;
  /** Callback when the cluster is toggled open/closed */
  onToggleInline: (clusterId: string) => void;
  /** Callback to expand cluster (fetch documents) */
  onExpand?: (cluster: ServerTimelineClusterType) => void;
  /** Callback to collapse expanded cluster */
  onCollapse?: (cluster: ServerTimelineClusterType) => void;
  /** Callback to load more documents in expanded cluster */
  onLoadMore?: (cluster: ServerTimelineClusterType) => void;
  /** Get expanded state for this cluster (if expanded) */
  getExpandedState?: (cluster: ServerTimelineClusterType) => ExpandedClusterState | undefined;
  /** Check if this cluster is expanded */
  isExpanded?: (cluster: ServerTimelineClusterType) => boolean;
  /** Current timeline granularity for date formatting */
  granularity?: TimelineGranularity;
  /** Additional CSS classes */
  className?: string;
  /**
   * Everything a document card needs, built once by the parent timeline — the
   * same builder the day bands and the max-zoom list use. One callback rather
   * than a dozen individual props, so a new card capability reaches every
   * rendering at once instead of being threaded through each separately.
   */
  nodeProps: (doc: DocumentRowMapped) => DocumentNodeProps;
}

/**
 * ServerTimelineCluster - Server-side aggregation cluster component
 *
 * Displays a cluster from server-side buckets.
 * Documents are lazy-loaded when the cluster is expanded, supporting
 * 6000+ document timelines.
 *
 * Key differences from client-side TimelineCluster:
 * - Only shows document COUNT until expanded
 * - Fetches actual documents on demand via store
 * - Supports pagination within expanded clusters
 *
 * @example
 * ```tsx
 * <ServerTimelineCluster
 *   cluster={serverCluster}
 *   side="right"
 *   pixelPosition={200}
 *   isInlineOpen={openClusterId === serverCluster.id}
 *   onToggleInline={(id) => setOpenClusterId(prev => prev === id ? null : id)}
 *   onExpand={(c) => store.expandCluster(buildingId, c.startTime, c.endTime)}
 *   getExpandedState={(c) => store.getExpandedCluster(c.startTime, c.endTime)}
 * />
 * ```
 */
export const ServerTimelineCluster = observer(function ServerTimelineCluster({
  cluster,
  side,
  pixelPosition,
  isInlineOpen,
  onToggleInline,
  onExpand,
  onCollapse: _onCollapse,
  onLoadMore,
  getExpandedState,
  isExpanded,
  granularity,
  className,
  nodeProps,
}: ServerTimelineClusterProps) {
  const { t, language } = useLanguage();
  const count = cluster.documentCount;
  const dateLocale = language === "et" ? et : undefined;

  // Month/day names are localized via the date-fns locale so they render in the
  // active language. The range itself is derived in the utils, where the
  // half-open bucket edges are unit-tested.
  const dateRange = formatClusterDateRange(cluster, granularity, dateLocale);

  // Get expanded state for this cluster (if any)
  const expandedState = getExpandedState?.(cluster);
  const clusterIsExpanded = isExpanded?.(cluster) ?? false;

  const handleClick = useCallback(() => {
    if (!clusterIsExpanded) {
      // First click - fetch documents
      onExpand?.(cluster);
    }
    // Toggle the inline list open/closed (parent manages which one is open)
    onToggleInline(cluster.id);
  }, [cluster, clusterIsExpanded, onExpand, onToggleInline]);

  const handleLoadMore = useCallback(() => {
    onLoadMore?.(cluster);
  }, [cluster, onLoadMore]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  // Ref for scroll container to detect scroll position
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle scroll-based loading for expanded cluster
  const handleExpandedScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !expandedState) return;

    const { hasMore, isLoading } = expandedState;
    if (!hasMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when within 100px of bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      handleLoadMore();
    }
  }, [expandedState, handleLoadMore]);

  // Render document list for expanded cluster
  const renderExpandedContent = () => {
    if (!expandedState) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    const { documents, totalCount, hasMore, isLoading, truncated } = expandedState;

    // Show loading spinner while fetching initial documents
    if (isLoading && documents.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          <p className="mt-2 text-sm text-muted-foreground">{t.timeline.loadingDocuments}</p>
        </div>
      );
    }

    // Handle wheel events on the scrollable area - stop propagation to prevent
    // parent timeline zoom from capturing the events
    const handleWheel = (e: React.WheelEvent) => {
      e.stopPropagation();
    };

    return (
      <div className="flex flex-col">
        <p className="mb-1 border-b px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {t.timeline.clusterDocumentsOf
            .replace("{loaded}", String(documents.length))
            .replace("{total}", String(totalCount))}
        </p>
        {/* Native scrollable div with scroll-based loading */}
        <div
          ref={scrollContainerRef}
          className="max-h-[400px] overflow-y-auto overscroll-contain"
          onWheel={handleWheel}
          onScroll={handleExpandedScroll}
        >
          <div className="flex flex-col gap-1 p-2">
            {documents.map((doc) => (
              <ServerTimelineNode
                key={doc.id}
                side={side}
                pixelPosition={0}
                variant="compact"
                {...nodeProps(doc)}
              />
            ))}
            {/* Loading indicator at bottom when fetching more */}
            {(hasMore || truncated) && isLoading && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="ml-2 text-xs text-muted-foreground">{t.timeline.loadingMore}</span>
              </div>
            )}
            {/* Paging gave up short of the window's end (a cursor the server
                rejected twice over), so the rest is still there to be read.
                A button, not the scroll handler above: a re-read that keeps
                dead-ending would otherwise refetch on every scroll event. */}
            {truncated && !isLoading && (
              <div className="flex justify-center py-2">
                <Button variant="ghost" size="sm" onClick={handleLoadMore}>
                  {t.timeline.loadMore}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the cluster card content
  const clusterCard = (
    <div
      role="button"
      tabIndex={0}
      aria-label={t.timeline.clusterAriaLabel
        .replace("{count}", String(count))
        .replace("{dateRange}", dateRange)
        .replace(
          "{action}",
          clusterIsExpanded ? t.timeline.clusterActionView : t.timeline.clusterActionExpand
        )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "group flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 shadow-sm transition-all duration-200",
        // A cluster is a container of cards, so it sits a step darker than a
        // single document card (TIMELINE_DOCUMENT_CARD_FILL_CLASS) — but only a step: a mid grey buried
        // the hierarchy instead of showing it. Its border darkens to match, so
        // the card still reads as its own component.
        "border-[#d8d8da] bg-[#e5e7eb]",
        "hover:bg-[#dadde1] hover:shadow-lg",
        isInlineOpen && "shadow-md"
      )}
    >
      {/* Files icon with background */}
      <div className="flex-shrink-0 rounded-lg bg-muted p-2.5">
        <Files className="h-5 w-5 text-muted-foreground" />
      </div>

      {/* Content — text stays pinned to the foreground tone (rather than
          muted-foreground) so it keeps a wide margin against the grey fill. */}
      <div className="flex-1">
        <p className="text-sm font-semibold text-foreground">
          {count} {count === 1 ? t.timeline.documentSingular : t.timeline.documentPlural}
        </p>
        <p className="text-xs text-foreground/80">{dateRange}</p>
      </div>

      {/* Expand/collapse arrow */}
      {clusterIsExpanded ? (
        <ChevronDown
          className={cn(
            "h-4 w-4 text-foreground/80 transition-transform duration-200",
            isInlineOpen && "rotate-180"
          )}
        />
      ) : (
        <ChevronRight className="h-4 w-4 text-foreground/80 transition-transform group-hover:translate-x-0.5" />
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "absolute",
        side === "left" ? "left-6 right-1/2 pr-2" : "left-1/2 right-6 pl-2",
        isInlineOpen && "z-30",
        className
      )}
      style={{ top: pixelPosition, transform: "translateY(-50%)" }}
    >
      {/* Card row - this determines the wrapper height for translateY(-50%) centering */}
      <div className="relative">
        <div
          className="flex items-center"
          style={{ flexDirection: side === "left" ? "row-reverse" : "row" }}
        >
          {/* Connector line */}
          <TimelineConnector direction={side} />

          {/* Cluster dot (larger and slate colored) */}
          <TimelineDot variant="cluster" size="lg" />

          {/* Cluster card */}
          <div className={cn("relative min-w-0 flex-1", side === "left" ? "mr-3" : "ml-3")}>
            {clusterCard}

            {/* Anchor the expanded list to the card column, not the full row that
                also contains the connector and stem dot. This keeps compact
                document cards aligned with ordinary timeline cards at every
                viewport width without affecting the wrapper's centered height. */}
            {isInlineOpen && (
              <div className="absolute left-0 right-0 top-full mt-2 rounded-xl border border-border bg-card/95 shadow-lg backdrop-blur-sm">
                {renderExpandedContent()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
